import React, { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { AlertTriangle, Info, OctagonAlert } from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { sourceLabel } from './sourceLabel';

/**
 * Rådgivande momsnotiser — momsbefrielsegränsen (G10) och satsändringar (G13).
 *
 * ADVISORY, ALWAYS. Nothing here blocks a period, changes a setting, or
 * registers anything with Skatteverket. It says what the ledger implies and
 * links the rule; the user decides.
 *
 * Fetched ONCE per page load, from one endpoint. These are calendar-year
 * aggregates that change slowly, and polling them would cost more than the
 * information is worth.
 */
const VARIANT = { alert: 'danger', warning: 'warning', info: 'light' };
const ICON = { alert: OctagonAlert, warning: AlertTriangle, info: Info };

export default function VatAdvisoryRail({ companyId }) {
    const [advisories, setAdvisories] = useState([]);

    useEffect(() => {
        let cancelled = false;
        axios.get(getApiUrl(`${companyId}/reporting/vat-advisories`), { withCredentials: true })
            .then((response) => {
                if (!cancelled && response.data?.success) {
                    setAdvisories(response.data.advisories || []);
                }
            })
            // A failed advisory fetch must never take the period list with it.
            .catch((error) => console.error('VAT advisory fetch error:', error));
        return () => { cancelled = true; };
    }, [companyId]);

    if (advisories.length === 0) return null;

    return (
        <div className="mb-3">
            <style>{`
                /* This stylesheet paints .alert-warning as #FFAA2B text on a pale orange
                   ground — yellow on yellow, reported as unreadable. Same family as the
                   .alert-success neon fixed earlier. Amber stays the signal colour, but on a
                   readable slate body. */
                .vat-advisory .alert-warning,
                .vat-advisory.alert-warning {
                  color: #78350f;
                  background: #fffbeb;
                  border-color: #fcd34d;
                }
                .vat-advisory .alert-light,
                .vat-advisory.alert-light {
                  color: #475569;
                  background: #f8fafc;
                  border-color: #e2e8f0;
                }
            `}</style>
            {advisories.map((advisory, index) => {
                const Icon = ICON[advisory.severity] || Info;
                return (
                    <Alert
                        key={`${advisory.type}-${index}`}
                        variant={VARIANT[advisory.severity] || 'light'}
                        className="vat-advisory d-flex align-items-start"
                    >
                        <Icon size={18} className="me-2 mt-1 flex-shrink-0" />
                        <div>
                            {advisory.message}
                            {advisory.sourceUrl && (
                                <>
                                    {' '}
                                    <a href={advisory.sourceUrl} target="_blank" rel="noopener noreferrer">
                                        {sourceLabel(advisory.sourceUrl)}
                                    </a>
                                </>
                            )}
                        </div>
                    </Alert>
                );
            })}
        </div>
    );
}
